// Hitter Truth — due list / lucky list from team-derived expected AVG, plus
// traffic conversion and two-strike battle leaders.

import React from 'react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Card, shortName } from './ui';
import type { HitterTruthResult, DueFlag } from '../../lib/hitter-truth';

const FlagRow = ({ flag, first }: { flag: DueFlag; first: boolean }) => (
  <div className="flex items-center gap-3 px-4 py-3" style={{ borderTop: first ? undefined : `1px solid ${GS.line}` }}>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-extrabold" style={{ color: GS.ink }}>{shortName(flag.player?.name || '?')}</p>
      <p className="text-[10px] font-semibold mt-0.5 leading-snug" style={{ color: GS.ink2 }}>
        {flag.verdict === 'due'
          ? 'Contact quality says better days ahead'
          : "Soft contact finding grass. Don't chase the average"}
      </p>
    </div>
    <div className="text-right shrink-0">
      <div className="text-sm font-black" style={{ color: GS.ink }}>{fmtStat(flag.actualAvg, 'avg3')}</div>
      <div className="text-[9px] font-extrabold" style={{ color: GS.ink3 }}>expect ≈ {fmtStat(flag.expectedAvg, 'avg3')}</div>
    </div>
    <span
      className="shrink-0 text-[8.5px] font-black uppercase rounded-lg px-2 py-1"
      style={flag.verdict === 'due'
        ? { background: GS.greenSoft, color: GS.greenInk, border: `1px solid ${GS.green}`, letterSpacing: '0.07em' }
        : { background: GS.goldSoft, color: GS.goldInk, border: `1px solid ${GS.gold}`, letterSpacing: '0.07em' }}
    >
      {flag.verdict === 'due' ? '▲ Due' : 'Riding luck'}
    </span>
  </div>
);

export const DueListCard = ({ truth }: { truth: HitterTruthResult }) => {
  const flags = truth.flags.slice(0, 4);
  const traffic = truth.traffic[0];
  const battle = truth.battle[0];
  const hasFooter = traffic || battle;

  if (!truth.rates && !hasFooter) {
    return (
      <Card className="p-5">
        <p className="text-sm font-black" style={{ color: GS.ink }}>Not enough contact data yet</p>
        <p className="text-[11px] font-semibold mt-1 leading-relaxed" style={{ color: GS.ink2 }}>
          The due list needs batted-ball data (line-drive / ground-ball rates) from your GameChanger imports.{' '}
          <b style={{ color: GS.greenInk }}>Re-import your latest season CSV</b> and Dugout starts telling you who's better than their average.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      {flags.map((f, i) => <FlagRow key={f.playerId} flag={f} first={i === 0} />)}
      {flags.length === 0 && truth.rates && (
        <p className="px-4 py-3 text-[11px] font-semibold" style={{ color: GS.ink2 }}>
          No one is running far from their expected average right now. Results are matching the contact quality.
        </p>
      )}
      {hasFooter && (
        <div className="flex" style={{ borderTop: `1px solid ${GS.line}` }}>
          {traffic && (
            <div className="flex-1 px-4 py-3">
              <div className="text-[15px] font-black" style={{ color: GS.ink }}>
                {fmtStat(traffic.conversion, 'pct')} <span className="text-[10px] font-bold" style={{ color: GS.ink2 }}>{shortName(traffic.player?.name || '?')}</span>
              </div>
              <div className="text-[8.5px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>Best at cashing runners in</div>
            </div>
          )}
          {battle && (
            <div className="flex-1 px-4 py-3" style={{ borderLeft: traffic ? `1px solid ${GS.line}` : undefined }}>
              <div className="text-[15px] font-black" style={{ color: GS.ink }}>
                {fmtStat(battle.battlePct, 'pct')} <span className="text-[10px] font-bold" style={{ color: GS.ink2 }}>{shortName(battle.player?.name || '?')}</span>
              </div>
              <div className="text-[8.5px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>Two-strike battle rate</div>
            </div>
          )}
        </div>
      )}
      {truth.rates && (
        <p className="px-4 py-2.5 text-[10px] font-semibold leading-relaxed" style={{ color: GS.ink3, borderTop: `1px solid ${GS.line}` }}>
          Expected AVG comes from <b style={{ color: GS.ink }}>your team's own batted-ball results</b> ({truth.rates.bip} balls in play). No league tables, no guesswork.
        </p>
      )}
    </Card>
  );
};
